import type {
  TrainingEvalRunView,
  TrainingSessionCheckpointView,
  TrainingSessionPoliciesView,
} from "@/api/trainingEval";
import type { JsonObject } from "@/api/common";
import {
  mlflowRunDeepLink,
  parseCheckpointTag,
  type CheckpointTag,
} from "@/modules/training/public";
import { formatCompactCount, formatNumber } from "@/utils/formats";

function stringParam(params: JsonObject | null, key: string): string | null {
  const value = params?.[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function latestCheckpoint(
  policies: TrainingSessionPoliciesView | null,
): TrainingSessionCheckpointView | null {
  const checkpoints = policies?.policies ?? [];
  if (checkpoints.length === 0) return null;
  let latest: TrainingSessionCheckpointView | null = null;
  let latestValue = -Infinity;
  for (const checkpoint of checkpoints) {
    const parsed = parseCheckpointTag(checkpoint.epoch);
    if (parsed !== null && parsed.value > latestValue) {
      latest = checkpoint;
      latestValue = parsed.value;
    }
  }
  return latest ?? checkpoints.at(-1) ?? null;
}

function checkpointTagFromText(text: string | null): CheckpointTag | null {
  if (text === null) return null;
  const segments = text.split(/[/\\]/);
  for (let index = segments.length - 1; index >= 0; index--) {
    const parsed = parseCheckpointTag(segments[index]);
    if (parsed !== null) return parsed;
  }
  return null;
}

export function evaluatedCheckpointFromParams(params: JsonObject | null): CheckpointTag | null {
  return (
    checkpointTagFromText(stringParam(params, "policy_checkpoint")) ??
    checkpointTagFromText(stringParam(params, "artifact_uri"))
  );
}

export function checkpointDisplay(tag: CheckpointTag): { text: string; title: string | null } {
  if (tag.unit === "step") {
    return {
      text: `step ${formatCompactCount(tag.value)}`,
      title: `step ${formatNumber(tag.value)}`,
    };
  }
  return { text: `epoch ${tag.value}`, title: null };
}

export function evaluatedArtifact(
  detail: TrainingEvalRunView | null,
  policies: TrainingSessionPoliciesView | null,
): string | null {
  return (
    stringParam(detail?.params ?? null, "policy_checkpoint") ??
    stringParam(detail?.params ?? null, "artifact_uri") ??
    latestCheckpoint(policies)?.artifact_uri ??
    null
  );
}

export function mlflowRunUrl(
  runId: string | null,
  policies: TrainingSessionPoliciesView | null,
): string | null {
  if (policies === null) return null;
  return mlflowRunDeepLink(policies.mlflow_ui_url, policies.experiment_id, runId);
}
